import { t } from "@lingui/macro";
import { Tag } from "@phosphor-icons/react";
import type { URL } from "@reactive-resume/schema";
import { urlSchema } from "@reactive-resume/schema";
import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
} from "@reactive-resume/ui";
import { forwardRef, useEffect, useMemo, useState } from "react";
import type { KeyboardEvent } from "react";

type Props = {
  id?: string;
  value: URL;
  placeholder?: string;
  onChange: (value: URL) => void;
  /** e.g. react-hook-form `field.onBlur` */
  onBlur?: () => void;
};

export const URLInput = forwardRef<HTMLInputElement, Props>(
  ({ id, value, placeholder, onChange, onBlur }, ref) => {
    const [draftHref, setDraftHref] = useState(value.href);

    useEffect(() => {
      setDraftHref(value.href);
    }, [value.href]);

    const candidate = useMemo(() => ({ ...value, href: draftHref }), [value, draftHref]);
    const hasError = useMemo(() => !urlSchema.safeParse(candidate).success, [candidate]);

    const commitHrefIfValid = () => {
      const next = { ...value, href: draftHref };
      if (urlSchema.safeParse(next).success) {
        onChange(next);
      } else {
        setDraftHref(value.href);
      }
      onBlur?.();
    };

    const onHrefKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();
        // Blur commits via onBlur (single path, avoids double onBlur / double commit).
        event.currentTarget.blur();
      }
    };

    return (
      <>
        <div className="flex gap-x-1">
          <Input
            ref={ref}
            id={id}
            value={draftHref}
            className="flex-1"
            hasError={hasError}
            placeholder={placeholder}
            onChange={(event) => {
              setDraftHref(event.target.value);
            }}
            onBlur={commitHrefIfValid}
            onKeyDown={onHrefKeyDown}
          />

          <Popover>
            <Tooltip content={t`Label`}>
              <PopoverTrigger asChild>
                <Button size="icon" variant="ghost">
                  <Tag />
                </Button>
              </PopoverTrigger>
            </Tooltip>
            <PopoverContent className="p-1.5">
              <Input
                value={value.label}
                placeholder={t`Label`}
                onChange={(event) => {
                  onChange({ ...value, label: event.target.value });
                }}
              />
            </PopoverContent>
          </Popover>
        </div>

        {hasError && (
          <small className="opacity-75">{t`Enter a valid URL (e.g. https://example.com) or leave empty.`}</small>
        )}
      </>
    );
  },
);
